import { useMemo, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';

import { SurveyQuestion } from '../../../../../../../../../../../backend/src/types/types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Checkbox } from '@/new/components/shadcn/checkbox';
import { Input } from '@/new/components/shadcn/input';
import { RadioGroup, RadioGroupItem } from '@/new/components/shadcn/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';

const OTHER_VALUE = '__other__';

type Selections = Record<string, string[]>; // questionId -> selected option values
type FreeText = Record<string, string>; // questionId -> "Other" text

function formatAnswers(questions: SurveyQuestion[], selections: Selections, freeText: FreeText): string {
  return questions
    .map((q) => {
      const selected = selections[q.id] ?? [];
      const parts = selected.filter((value) => value !== OTHER_VALUE).map((value) => value);
      if (selected.includes(OTHER_VALUE)) {
        const text = (freeText[q.id] ?? '').trim();
        if (text) parts.push(`Other: ${text}`);
      }
      return `Q: ${q.question}\nA: ${parts.join(', ')}`;
    })
    .join('\n\n');
}

export function SurveyBlock({
  questions,
  onSubmit,
  onDismiss,
  disabled,
}: {
  questions: SurveyQuestion[];
  onSubmit: (formatted: string) => void;
  onDismiss: () => void;
  disabled?: boolean;
}) {
  const [selections, setSelections] = useState<Selections>({});
  const [freeText, setFreeText] = useState<FreeText>({});
  const [activeTab, setActiveTab] = useState(questions[0]?.id ?? '');

  const toggle = (question: SurveyQuestion, value: string) => {
    setSelections((prev) => {
      const current = prev[question.id] ?? [];
      if (question.multiSelect) {
        const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
        return { ...prev, [question.id]: next };
      }
      return { ...prev, [question.id]: [value] };
    });
  };

  const isQuestionAnswered = (q: SurveyQuestion) => {
    const selected = selections[q.id] ?? [];
    if (selected.length === 0) return false;
    if (selected.includes(OTHER_VALUE) && (freeText[q.id] ?? '').trim() === '') return false;
    return true;
  };

  const isComplete = useMemo(() => questions.every(isQuestionAnswered), [questions, selections, freeText]);

  const handleSubmit = () => {
    if (!isComplete || disabled) return;
    onSubmit(formatAnswers(questions, selections, freeText));
  };

  const unansweredLabels = useMemo(
    () =>
      questions.reduce<string[]>((acc, q) => {
        if (!isQuestionAnswered(q)) acc.push(`'${q.title}'`);
        return acc;
      }, []),
    [questions, selections, freeText],
  );

  const activeIndex = questions.findIndex((q) => q.id === activeTab);
  const isLast = activeIndex === questions.length - 1;

  const goNext = () => {
    const next = questions[activeIndex + 1];
    if (next) setActiveTab(next.id);
  };

  const goPrev = () => {
    const prev = questions[activeIndex - 1];
    if (prev) setActiveTab(prev.id);
  };

  return (
    <div className="w-full rounded-xl border bg-white px-3.5 py-2.5">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="mb-2.5 flex items-center justify-between gap-2">
          <TabsList className="h-8">
            {questions.map((q) => {
              const answered = isQuestionAnswered(q);
              return (
                <TabsTrigger
                  key={q.id}
                  value={q.id}
                  disabled={disabled}
                  className={cn(answered && q.id !== activeTab && 'text-gray-300', 'py-1.75 text-xs')}
                >
                  {q.title}
                </TabsTrigger>
              );
            })}
          </TabsList>
          <Button type="button" variant={'ghost'} size={'iconSm'} onClick={onDismiss} aria-label="Dismiss survey">
            <Icon className="lucide--x" />
          </Button>
        </div>

        {questions.map((question) => {
          const selected = selections[question.id] ?? [];
          const otherSelected = selected.includes(OTHER_VALUE);
          return (
            <TabsContent key={question.id} value={question.id} className="px-1 py-2">
              <p className="mb-3.5 px-1.5 text-sm font-medium text-gray-900">{question.question}</p>

              {question.multiSelect ? (
                <div className="flex flex-col gap-1.5">
                  {question.choices.map((option) => (
                    <OptionRow
                      key={option}
                      label={option}
                      value={option}
                      multiSelect
                      selected={selected.includes(option)}
                      onClick={() => toggle(question, option)}
                      disabled={disabled}
                    />
                  ))}
                  {question.showFreeText && (
                    <>
                      <OptionRow
                        label="Other"
                        value={OTHER_VALUE}
                        multiSelect
                        selected={otherSelected}
                        onClick={() => toggle(question, OTHER_VALUE)}
                        disabled={disabled}
                      />
                      <OtherInput
                        show={otherSelected}
                        value={freeText[question.id] ?? ''}
                        onChange={(value) => setFreeText((prev) => ({ ...prev, [question.id]: value }))}
                        disabled={disabled}
                      />
                    </>
                  )}
                </div>
              ) : (
                <RadioGroup
                  value={selected[0] ?? ''}
                  onValueChange={(value) => toggle(question, value)}
                  className="flex flex-col gap-1.5"
                  disabled={disabled}
                >
                  {question.choices.map((option) => (
                    <OptionRow
                      key={option}
                      label={option}
                      value={option}
                      multiSelect={false}
                      selected={selected.includes(option)}
                      onClick={() => toggle(question, option)}
                      disabled={disabled}
                    />
                  ))}
                  {question.showFreeText && (
                    <>
                      <OptionRow
                        label="Other"
                        value={OTHER_VALUE}
                        multiSelect={false}
                        selected={otherSelected}
                        onClick={() => toggle(question, OTHER_VALUE)}
                        disabled={disabled}
                      />
                      <OtherInput
                        show={otherSelected}
                        value={freeText[question.id] ?? ''}
                        onChange={(value) => setFreeText((prev) => ({ ...prev, [question.id]: value }))}
                        disabled={disabled}
                      />
                    </>
                  )}
                </RadioGroup>
              )}
            </TabsContent>
          );
        })}
      </Tabs>

      <div className="mt-4 flex items-center justify-between gap-2 pb-1">
        <Button onClick={goPrev} disabled={activeIndex <= 0 || disabled} variant="ghost" size="sm">
          <Icon className="lucide--arrow-left" />
          Back
        </Button>
        {isLast ? (
          <Tooltip open={isComplete || disabled ? false : undefined}>
            <TooltipTrigger asChild>
              <span>
                <Button onClick={handleSubmit} disabled={!isComplete || disabled} variant="primary" size="sm">
                  Submit
                  <Icon className="lucide--arrow-right" />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Answer {new Intl.ListFormat('en', { style: 'long', type: 'conjunction' }).format(unansweredLabels)} to
              submit
            </TooltipContent>
          </Tooltip>
        ) : (
          <Button
            onClick={goNext}
            disabled={disabled || !isQuestionAnswered(questions[activeIndex])}
            variant="primary"
            size="sm"
          >
            Next
            <Icon className="lucide--arrow-right" />
          </Button>
        )}
      </div>
    </div>
  );
}

function OptionRow({
  label,
  value,
  multiSelect,
  selected,
  onClick,
  disabled,
}: {
  label: string;
  value: string;
  multiSelect: boolean;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
}) {
  const classes = cn(
    'flex items-center gap-2.5 rounded-md border px-3 py-2 text-left text-sm transition-colors duration-150',
    selected
      ? 'border-teal-400 bg-teal-50/50 text-gray-900'
      : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300 hover:bg-gray-50/50',
    disabled && 'cursor-not-allowed opacity-60',
  );

  if (multiSelect) {
    return (
      <button type="button" onClick={onClick} disabled={disabled} className={classes}>
        <Checkbox checked={selected} tabIndex={-1} />
        <span>{label}</span>
      </button>
    );
  }

  return (
    <label className={cn(classes, 'cursor-pointer')}>
      <RadioGroupItem value={value} />
      <span>{label}</span>
    </label>
  );
}

function OtherInput({
  show,
  value,
  onChange,
  disabled,
}: {
  show: boolean;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <AnimatePresence initial={false}>
      {show && (
        <motion.div
          initial={{ height: 0, opacity: 0, marginTop: '-0.375rem' }}
          animate={{ height: 'auto', opacity: 1, marginTop: 0 }}
          exit={{ height: 0, opacity: 0, marginTop: '-0.375rem' }}
          transition={{
            height: { duration: 0.1, delay: show ? 0.05 : 0 },
            marginTop: { duration: 0.1, delay: show ? 0.05 : 0 },
            opacity: { duration: 0.1, delay: show ? 0.05 : 0 },
          }}
          key={'input'}
        >
          <Input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Type your answer..."
            disabled={disabled}
            autoFocus
            className="ml-6 w-[calc(100%-1.5rem)]"
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
